import { InnspelApiError, type InnspelClient, type MySubmission } from '@innspel/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Banner, Btn, MySubmissions, useInnspel } from '../../lib/index.ts';
import { erOffline, hentMine, oppdaterSistKjent, type SistKjent } from '../../lib/widget/sistKjent.ts';
import type { Sesjonstilstand } from '../App.tsx';
import type { PortalKonfig } from '../konfig.ts';

/**
 * 3 — «Mine innspill» i fullskjerm. NØYAKTIG samme komponent som i dialogen
 * (`MySubmissions` fra pakken) — det er det snapshot-testen beviser. Krever
 * innlogging: anonym modus har ingen liste (kontrakten: 401 feedback_token_required).
 * Utløpt token → «Logg inn på nytt», lista står som sist hentet. Uten nett står lista fra
 * forrige henting i denne økten, med tidspunktet (H3.1, konseptsidens skjerm 3) — i minnet,
 * aldri i lagringen.
 */
export function Mine({ client, konfig, tilstand, onLoggInn, onUtlopt }: { client: InnspelClient; konfig: PortalKonfig; tilstand: Sesjonstilstand; onLoggInn: () => void; onUtlopt: () => void }) {
  const { open, isOpen } = useInnspel();
  const [rader, setRader] = useState<MySubmission[]>([]);
  const [sistKjentAt, setSistKjentAt] = useState<string | undefined>();
  const sistHentet = useRef<SistKjent | undefined>(undefined);
  const innlogget = tilstand === 'innlogget';
  const utlopt = tilstand === 'utlopt';
  const [laster, setLaster] = useState(innlogget);
  const [feil, setFeil] = useState<string | undefined>();

  const hent = useCallback(async () => {
    if (!innlogget) return;
    setLaster(true);
    setFeil(undefined);
    try {
      const r = await hentMine(client, sistHentet, erOffline());
      setRader(r.rader);
      setSistKjentAt(r.sistKjentAt);
    } catch (e) {
      // Utløpet eies av `App`: toppen, fanene og denne siden er tre tegn på det samme,
      // og de kan ikke si hver sin ting.
      if (e instanceof InnspelApiError && (e.code === 'feedback_token_expired' || e.code === 'feedback_token_required')) onUtlopt();
      else setFeil(e instanceof InnspelApiError ? e.message : 'tjeneren svarte ikke.');
    } finally {
      setLaster(false);
    }
  }, [client, innlogget, onUtlopt]);

  // Hentes ved innlasting, og på nytt hver gang dialogen lukkes — en innsending
  // skal stå i lista straks. Én effekt, så det aldri går to kall samtidig.
  useEffect(() => {
    if (!isOpen) void hent();
  }, [isOpen, hent]);

  // Ett sted, to utganger: utløpt uten hentet liste (under) og utløpt med lista stående
  // (i returen). Ordlyden er kortet ned én gang alt — «Innloggingen din gikk ut (15 min)»
  // ble forkastet i UX-gjennomgangen 2026-09-14 (rad «Utløpt») — og to kopier er to steder
  // den kan gjenoppstå.
  const utloptVarsel = (
    <>
      <Banner ikon="time-outline" tone="warning" live testId="mine-utlopt">
        Innloggingen gikk ut. Logg inn på nytt for å se innspillene dine.
      </Banner>
      {konfig.loginRedirectUrl ? <Btn ikon="key-outline" testId="logg-inn-paa-nytt" label={`Logg inn på nytt hos ${konfig.tenantName}`} onClick={onLoggInn} /> : null}
    </>
  );

  if (tilstand === 'anonym') {
    return (
      <>
        <Banner ikon="key-outline" tone="info">
          «Mine innspill» finnes bare når du er logget inn hos {konfig.tenantName}. Anonyme innspill teller, men kan ikke følges.
        </Banner>
        {konfig.loginRedirectUrl ? <Btn ikon="key-outline" testId="logg-inn-hos" label={`Logg inn hos ${konfig.tenantName}`} onClick={onLoggInn} /> : null}
      </>
    );
  }

  // Utløpt uten at lista rakk å bli hentet: vi vet ingenting om innspillene hennes, og
  // tomtilstanden ville påstått at det ikke finnes noen. «En liste som feilet sier
  // «kunne ikke hentes», aldri «ingen»» (`ux-flow-architect`). Veien videre er
  // innloggingen, ikke en invitasjon til å sende noe nytt.
  if (utlopt && rader.length === 0) return utloptVarsel;

  return (
    <>
      {utlopt ? utloptVarsel : null}
      {feil ? (
        <Banner ikon="warning-outline" tone="danger" live>
          Kunne ikke hente innspillene dine: {feil} Viser det som sist ble hentet.
        </Banner>
      ) : null}
      <MySubmissions
        rader={rader}
        laster={laster}
        tenantName={konfig.tenantName}
        onNytt={() => void open()}
        overskriftNivaa={1}
        storage={client.storage}
        {...(sistKjentAt ? { sistKjentAt } : {})}
        leggTil={(id, text) => client.subjects.addAddendum(id, text)}
        onOppdatert={(sak) => {
          setRader((liste) => liste.map((r) => (r.pending !== true && r.id === sak.id ? sak : r)));
          oppdaterSistKjent(sistHentet, sak);
        }}
        minTextLength={client.settings.minTextLength}
        textMaxLength={client.settings.textMaxLength}
      />
    </>
  );
}
